import { Component, OnInit, Input, OnChanges, SimpleChanges, Self } from '@angular/core';
import { FormControl, ControlValueAccessor, Validator, ValidationErrors, NgControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { passwordStrengthValidator } from '@uw/shared/validators/password-strength.validator';
import { trigger, style, transition, animate } from '@angular/animations';
import { MatFormField, MatLabel, MatSuffix, MatInput, MatError } from '@angular/material/input';
import { NgIf, NgStyle } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatCard, MatCardHeader, MatCardContent } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'uw-password',
    templateUrl: './password.component.html',
    styleUrls: ['./password.component.scss'],
    animations: [
        trigger('slideOverlay', [
            transition(':enter', [
                style({ transform: 'translateY(-5px)', opacity: 0 }),
                animate('.10s cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'translateY(0)', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('.1s linear', style({ transform: 'translateY(-5px)', opacity: 0 }))
            ])
        ])
    ],
    imports: [ReactiveFormsModule, MatFormField, NgIf, NgStyle, MatLabel, MatIconButton, MatSuffix, MatIcon, MatInput, MatError, MatCard, MatCardHeader, MatCardContent, TranslateModule]
})
export class PasswordComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
  passwordFormControl: FormControl;
  isPasswordVisible = false;
  isFocused = false; // Track input focus

  // The function to trigger on value change
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  passwordStrengthValidator = passwordStrengthValidator();

  constructor(@Self() public controlDir: NgControl) {

    controlDir.valueAccessor = this;
    // Initialize the form control with the password field
    this.passwordFormControl = new FormControl('', [Validators.required, this.passwordStrengthValidator]);
  }

  ngOnInit(): void {

    this.passwordFormControl.valueChanges.subscribe(this.onChange);

    //get the parent control
    const control = this.controlDir.control;

    if (control) {
      if (control.validator) {
        this.passwordFormControl.addValidators(control.validator);
      }
      
      control.setValidators(this.passwordFormControl.validator);
  
      this.passwordFormControl.updateValueAndValidity();
      control.updateValueAndValidity();
    }
    
  }

  ngOnChanges(changes: SimpleChanges): void {    

    if (changes && 'passwordStrengthEnabled' in changes ) {
      if (!changes.passwordStrengthEnabled.currentValue) {
        this.passwordFormControl.removeValidators(this.passwordStrengthValidator);
        this.controlDir && this.controlDir.control && this.controlDir.control.removeValidators(passwordStrengthValidator);
      }      
    }

    this.passwordFormControl.updateValueAndValidity();
    this.controlDir && this.controlDir.control && this.controlDir.control.updateValueAndValidity();
  }

  @Input() label: string = ''; // Optional label to be used if needed
  @Input() maxlength: number = 16;
  @Input() passwordStrengthEnabled = true;

  // Writes the value from model to view
  writeValue(value: any): void {
    if (value) {
      this.passwordFormControl.setValue(value);
    } else {
      this.passwordFormControl.reset();
    }
  }

  // registers a callback function which will be called when the value in the view changes.
  // @see ngOnInit()
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  // registers a callback function which will be called when the value in the view is touched.
  // @see onBlur() 
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Validator method
  validate(): ValidationErrors | null {
    this.controlDir && this.controlDir.control && this.controlDir.control.setErrors(this.passwordFormControl.errors);
    return this.controlDir && this.controlDir.control && this.controlDir.control.errors;
  }

  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  onFocus() {
    this.isFocused = true;
  }

  onBlur() {
    if (!this.passwordFormControl.value) {
      setTimeout(() => (this.isFocused = false), 200); // Delay to prevent flicker when clicking inside
    }
    this.onTouched();
  }

  // Automatically mark all child controls as touched when the 
  // parent form calls markAllAsTouched().
  // This is needed as parent form controls markAllAsTouched does 
  // not mark the child controls as touched.
  markAsTouched(): void {
    this.passwordFormControl.markAsTouched();    
    this.onTouched(); // Notify the parent form that it was touched
  }
}
